/* qua.domains — Line Config
   A single live, persisted source of truth for the two line systems on the
   home page: the HERO field (organic streamlines + the continuous cable) and
   the SYSTEM rail (the routed PCB-style "kablo" in the Services section).

   Both renderers (study3-hero-field.js, study3-circuit.js) read from
   window.QUA_LINES at build time and re-render whenever it changes, so the
   Line Studio editor can tune everything live and edits persist across reloads.

   API:
     QUA_LINES.get()                 -> the live config object
     QUA_LINES.set('hero.width', 1)  -> deep-set + persist + notify
     QUA_LINES.set({ 'hero.width':1, 'rail.color':'#000' })  -> batch
     QUA_LINES.onChange(fn)          -> subscribe; returns an unsubscribe fn
     QUA_LINES.reset('hero'|'rail'|all) -> restore defaults for a scope
     QUA_LINES.defaults              -> a deep copy of the factory defaults
     QUA_LINES.exportJSON()          -> JSON string of current overrides-merged config
   Colours: hero.* colours are "r,g,b" strings (used inside rgba()); rail.*
   colours are hex (used directly as SVG stroke/fill).                         */
(function () {
  var KEY = "qua-lines-config-continuity";

  var DEFAULTS = {
    hero: {
      /* ---- streamline field (the lines behind the hero) ---- */
      leftCount: 11,        // streamlines seeded down the left edge
      rightCount: 13,       // streamlines seeded down the right edge
      lenFwd: 90,           // trace steps forward from each seed (length)
      lenBack: 60,          // trace steps backward (length the other way)
      step: 9,              // px per trace step (segment length)
      width: 0.8,           // base stroke width
      widthAccent: 1.0,     // stroke width for blue/orange accent lines
      opacity: 0.26,        // base line opacity
      opacityAccent: 0.42,  // accent line opacity
      turbulence: 1.0,      // overall bend/curl amount (flow-field amplitude)
      scale: 1.0,           // flow-field frequency (smaller = longer lazy curves)
      bias: 0.0,            // constant direction added to every point (radians-ish, 0..1 = 0..PI)
      drift: 2.2,           // live wobble amplitude
      speed: 1.0,           // animation speed multiplier
      sideInset: 0.0,       // push seeds further toward the edges (0..0.15 of W)
      micro: 26,            // count of micro dot/plus marks
      seed: 1,              // RNG seed — change to reshuffle the field deterministically
      colMain: "120,134,150",
      colBlue: "142,180,222",
      colOrange: "236,168,112",
      accentRatioBlue: 0.14,   // share of lines that are blue
      accentRatioOrange: 0.10, // additional share that are orange
      /* ---- the continuous cable (status card -> top -> left edge -> rail) ---- */
      cableWidth: 1.4,
      cableColor: "22,49,74",
      cableOpacity: 0.30,
      cableRadius: 28,        // corner fillet radius
      cablePackets: 2,        // travelling signal dots
      cablePacketSpeed: 1.0,
      cablePacketSize: 1.0,
      cablePts: null,         // null = auto-route from DOM; or [[fx,fy],...] fractions (0..1) once edited
      drawColor: "90,104,120", // colour of freehand-drawn lines
      drawWidth: 1.5,          // width of freehand-drawn lines
      userLines: []           // freehand strokes you draw: [ [[fx,fy],...], ... ]
    },
    rail: {
      /* ---- the system "kablo" (Services section routed trace) ---- */
      show: true,
      pts: null,              // null = auto-route from layout; or [[fx,fy],...] fractions once edited
      color: "#22384e",       // main trace + node strokes (NAVY)
      teal: "#5f97a3",        // junction cores (TEAL)
      warm: "#8b97a0",        // origin core + travelling packets (WARM)
      spineWarm: "#cf8a52",   // the one pale-orange travelling packet on the spine
      width: 1.0,             // stroke-width multiplier (1 = current)
      opacity: 1.0,           // opacity multiplier (1 = current)
      corner: 24,             // corner fillet radius
      extendTop: 0,           // px the spine reaches UP above the Services section (into the hero, behind it)
      packetSpeed: 1.0,       // travelling-packet speed multiplier
      packetSize: 1.0,        // travelling-packet radius multiplier
      junction: 1.0,          // junction/node radius multiplier
      scatter: true           // faint origin scatter dots
    }
  };

  /* ============================================================
     COMMITTED — routes finalised in the Line Studio editor and baked
     in here so the hero cable + system rail render correctly WITHOUT
     any localStorage (i.e. on Shopify, and on any duplicated site).
     Merged over DEFAULTS below; localStorage (if present) still wins,
     so live editing keeps working. To update after re-editing:
     run  QUA_LINES.exportJSON()  and replace this object's hero/rail.
     ============================================================ */
  var COMMITTED = {
    "hero": {
      "leftCount": 11,
      "rightCount": 13,
      "lenFwd": 90,
      "lenBack": 60,
      "step": 9,
      "width": 0.8,
      "widthAccent": 1,
      "opacity": 0.26,
      "opacityAccent": 0.42,
      "turbulence": 1,
      "scale": 1,
      "bias": 0,
      "drift": 2.2,
      "speed": 1,
      "sideInset": 0,
      "micro": 26,
      "seed": 1,
      "colMain": "120,134,150",
      "colBlue": "142,180,222",
      "colOrange": "236,168,112",
      "accentRatioBlue": 0.14,
      "accentRatioOrange": 0.1,
      "cableWidth": 1.4,
      "cableColor": "22,49,74",
      "cableOpacity": 0.3,
      "cableRadius": 20,
      "cablePackets": 2,
      "cablePacketSpeed": 1,
      "cablePacketSize": 1,
      "cablePts": [
        [
          0.005221932114882507,
          1.0564971751412429
        ],
        [
          0.155789764359352,
          0.2350223634651601
        ]
      ],
      "drawColor": "90,104,120",
      "drawWidth": 1.5,
      "userLines": [],
      "hidden": []
    },
    "rail": {
      "show": true,
      "pts": [
        [
          0.7025930647559968,
          0.05081954161400436
        ],
        [
          0.5513521161284544,
          0.05093398883393211
        ],
        [
          0.5225796242874381,
          0.014763691370679618
        ],
        [
          0.4575476439855556,
          0.014480061303902152
        ],
        [
          0.41053870917000673,
          -0.02195396236179254
        ],
        [
          -0.006042123046322748,
          -0.022556054257934176
        ],
        [
          -0.006806640625,
          0.41131916734772367
        ],
        [
          -0.005904680596296525,
          0.7475368967885115
        ],
        [
          -0.031136220895847753,
          0.752020242230029
        ],
        [
          -0.028821400684879848,
          0.7830926624404128
        ],
        [
          0.00120615773927085,
          0.7848591304001672
        ],
        [
          0.02440137963448611,
          0.806402575560045
        ],
        [
          0.929206729596605,
          0.805297911089438
        ],
        [
          0.9698860778977882,
          0.7790919857090254
        ],
        [
          0.9709964682177372,
          0.7489450951902312
        ],
        [
          0.6664891032780551,
          0.7495347471711633
        ],
        [
          0.6654329665568011,
          0.6650901147457778
        ],
        [
          0.5272092647093739,
          0.6648562443398386
        ],
        [
          0.526826171875,
          0.5818815743940536
        ],
        [
          0.6670703125,
          0.5818578722072898
        ],
        [
          0.666669948607037,
          0.661937840231681
        ],
        [
          0.802976689967256,
          0.6626195475851636
        ],
        [
          0.8021549479166666,
          0.5680963825722561
        ]
      ],
      "color": "#7d7d7d",
      "teal": "#5f97a3",
      "warm": "#8b97a0",
      "spineWarm": "#c29875",
      "width": 0.6,
      "opacity": 1,
      "corner": 20,
      "extendTop": 460,
      "packetSpeed": 1,
      "packetSize": 1,
      "junction": 1,
      "scatter": true,
      "nodeOv": {
        "jcard0": {
          "dx": -0.671875,
          "dy": 26.0859375
        },
        "jcard1": {
          "dx": -0.2265625,
          "dy": 27.2734375
        },
        "jcol": {
          "dx": -82.609375,
          "dy": -146.9921875,
          "r": 4.8,
          "color": "#8eb4de",
          "hidden": true
        },
        "jrec": {
          "dx": 0,
          "dy": 0.2265625,
          "r": 4.6
        },
        "jdom": {
          "r": 5,
          "color": "#f5f0e6",
          "hidden": true
        },
        "jweb": {
          "dx": 0.109375,
          "dy": 0,
          "r": 5
        },
        "origin": {
          "dx": -34.3828125,
          "dy": 0.4765625
        }
      },
      "extraNodes": [
        [
          0.66625,
          0.6245905447236562,
          5.2,
          "#3f7c89"
        ]
      ],
      "armOv": {
        "armW": {
          "dx1": -124.578125,
          "dy1": 2.0625,
          "dx2": -119.96875,
          "dy2": -0.234375
        },
        "armR": {
          "dx2": 128.7109375,
          "dy2": 0.2109375,
          "dx1": -32.625,
          "dy1": 0.0078125
        }
      }
    }
  };

  function deepCopy(o) { return JSON.parse(JSON.stringify(o)); }
  function deepMerge(base, over) {
    if (!over || typeof over !== "object") return base;
    Object.keys(over).forEach(function (k) {
      if (over[k] && typeof over[k] === "object" && !Array.isArray(over[k]) &&
          base[k] && typeof base[k] === "object") deepMerge(base[k], over[k]);
      else base[k] = over[k];
    });
    return base;
  }

  var FACTORY = deepMerge(deepCopy(DEFAULTS), deepCopy(COMMITTED));
  var data = deepCopy(FACTORY);
  try {
    var saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (saved) deepMerge(data, saved);
  } catch (e) { /* ignore */ }

  var subs = [];
  function notify(scope) { subs.forEach(function (fn) { try { fn(scope, data); } catch (e) {} }); }
  function persist() { try { localStorage.setItem(KEY, JSON.stringify(data)); } catch (e) {} }

  function setOne(path, value) {
    var parts = path.split("."), o = data;
    for (var i = 0; i < parts.length - 1; i++) { if (typeof o[parts[i]] !== "object") o[parts[i]] = {}; o = o[parts[i]]; }
    o[parts[parts.length - 1]] = value;
    return parts[0];
  }

  window.QUA_LINES = {
    defaults: deepCopy(FACTORY),
    get: function () { return data; },
    getScope: function (s) { return data[s]; },
    set: function (path, value) {
      var scopes = {};
      if (typeof path === "object") Object.keys(path).forEach(function (p) { scopes[setOne(p, path[p])] = 1; });
      else scopes[setOne(path, value)] = 1;
      persist();
      Object.keys(scopes).forEach(function (s) { notify(s); });
    },
    onChange: function (fn) { subs.push(fn); return function () { subs = subs.filter(function (f) { return f !== fn; }); }; },
    reset: function (scope) {
      if (!scope || scope === "all") data = deepCopy(FACTORY);
      else data[scope] = deepCopy(FACTORY[scope]);
      persist();
      notify(scope || "all");
    },
    exportJSON: function () { return JSON.stringify(data, null, 2); }
  };
})();
